import { useState, useEffect } from 'react'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import ShareButtons from '@/components/ShareButtons'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import {
  Check,
  ShoppingCart,
  BookOpen,
  Layout,
  CreditCard,
  Wand2,
  Loader2,
} from 'lucide-react'
import { useScrollAnimation } from '@/hooks/use-scroll-animation'
import api from '@/services/api'

interface RawItem {
  id: string
  attributes: {
    id: string
    name: string
    description: string
    sku?: string
    thumbnail_url?: string
  }
  relationships?: {
    dynamic_products?: {
      data: Array<{ type: string; id: string }>
    }
    product_images?: {
      data: Array<{ type: string; id: string }>
    }
  }
}

interface DynamicProduct {
  id: string
  type: string
  attributes: {
    value: string
    width: string
    height: string
  }
}

interface DynamicLine {
  type: 'dynamic_lines'
  id: string
  attributes: {
    price: string
    dynamic_product_id: string
  }
}

interface ProductImage {
  type: 'product_image'
  id: string
  attributes: {
    image_url: string
  }
}

type IncludedItem = DynamicProduct | DynamicLine | ProductImage

interface Product {
  id: string
  name: string
  slug: string
  description: string
  price: string
  images: string[]
  level: string
  basePrice: number
  features: Array<
    string | { text: string; bold?: boolean; subtopics?: string[] }
  >
  sku: string
}

const ProductGrid = () => {
  const [products, setProducts] = useState<Product[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const { ref, isVisible } = useScrollAnimation()

  useEffect(() => {
    const companyId = import.meta.env.VITE_COMPANY_ID ?? ''

    async function fetchProducts() {
      try {
        const res = await api.get<{
          data: RawItem[]
          included?: IncludedItem[]
        }>('/b2c/public/products', {
          headers: { 'X-Current-Company-ID': companyId },
          params: {
            'page[number]': 1,
            'page[size]': 10,
            'q[dynamic_products_id_null]': 0,
            include:
              'dynamic_products,dynamic_products.dynamic_lines,product_images',
          },
        })

        const included = res.data.included || []

        const formattedProducts = res.data.data.map((item, index) => {
          const a = item.attributes

          let minPrice = 0

          const dynamicProductIds =
            item.relationships?.dynamic_products?.data.map(dp => dp.id) || []

          const relatedLines = included.filter(
            (inc): inc is DynamicLine =>
              inc.type === 'dynamic_lines' &&
              'dynamic_product_id' in inc.attributes &&
              dynamicProductIds.includes(
                (inc.attributes as { dynamic_product_id: string })
                  .dynamic_product_id,
              ),
          )

          if (relatedLines.length > 0) {
            const prices = relatedLines.map(line =>
              Number(line.attributes.price),
            )
            minPrice = Math.min(...prices)
          }

          const descriptionLines = a.description
            .split('\n')
            .filter(line => line.trim())
          const shortDescription = descriptionLines[0] || a.description

          const features: Array<
            string | { text: string; bold?: boolean; subtopics?: string[] }
          > = []

          descriptionLines.forEach((line, index) => {
            if (index === 0) return
            const trimmed = line.trim()
            features.push(trimmed)
          })

          let level = 'Essencial'
          const nameLower = a.name.toLowerCase()
          if (nameLower.includes('magnifique') || minPrice > 1000) {
            level = 'Luxo'
          } else if (
            nameLower.includes('virtuose') ||
            (minPrice > 600 && minPrice < 1000)
          ) {
            level = 'Premium'
          } else if (
            nameLower.includes('inspiração') ||
            nameLower.includes('inspiracao') ||
            (minPrice > 400 && minPrice <= 600)
          ) {
            level = 'Sofisticado'
          }

          const productImageIds =
            item.relationships?.product_images?.data.map(img => img.id) || []

          const galleryImages = included
            .filter(
              (inc): inc is ProductImage =>
                inc.type === 'product_image' &&
                productImageIds.includes(inc.id),
            )
            .map(img => img.attributes.image_url)

          const finalImages =
            galleryImages.length > 0
              ? galleryImages
              : a.thumbnail_url
                ? [a.thumbnail_url]
                : []

          const slug = a.id

          return {
            id: item.id,
            name: a.name,
            slug,
            description: shortDescription,
            price:
              minPrice > 0
                ? `A partir de R$ ${minPrice.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                : 'Consulte',
            images: finalImages,
            level,
            basePrice: minPrice,
            features: features.slice(0, features.length),
            sku: a.sku || item.id,
          }
        })

        const sortedProducts = formattedProducts.sort(
          (a, b) => a.basePrice - b.basePrice,
        )

        setProducts(sortedProducts)
      } catch (error) {
        console.error('Erro ao buscar produtos:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchProducts()
    console.log(products)
  }, [])

  useEffect(() => {
    if (products.length === 0) return

    const baseUrl = window.location.origin

    const productsStructuredData = products.map(product => ({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: `FotoLivro ${product.name}`,
      description: product.description,
      image: product.images,
      brand: {
        '@type': 'Brand',
        name: 'FotoLivro Store',
      },
      offers: {
        '@type': 'Offer',
        url: `${baseUrl}/#produtos`,
        priceCurrency: 'BRL',
        price: product.basePrice,
        priceValidUntil: '2025-12-31',
        availability: 'https://schema.org/InStock',
        seller: {
          '@type': 'Organization',
          name: 'FotoLivro Store',
        },
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.9',
        reviewCount: '127',
        bestRating: '5',
        worstRating: '1',
      },
      sku: product.sku,
      category: 'Fotolivros Personalizados',
      additionalProperty: [
        {
          '@type': 'PropertyValue',
          name: 'Nível',
          value: product.level,
        },
        {
          '@type': 'PropertyValue',
          name: 'Abertura',
          value: '180º panorâmica',
        },
      ],
    }))

    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.text = JSON.stringify(productsStructuredData)
    script.id = 'products-schema'

    const existingScript = document.getElementById('products-schema')
    if (existingScript) {
      existingScript.remove()
    }

    document.head.appendChild(script)

    return () => {
      const scriptToRemove = document.getElementById('products-schema')
      if (scriptToRemove) {
        scriptToRemove.remove()
      }
    }
  }, [products])

  return (
    <section
      id="produtos"
      className="py-24 bg-gradient-to-b from-muted/10 via-background to-muted/10"
    >
      <div
        ref={ref}
        className={`container mx-auto px-4 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}
      >
        <div className="mb-12">
          <div className="bg-secondary/5 border border-secondary/20 rounded-2xl p-6 max-w-7xl mx-auto">
            <h3 className="font-serif text-2xl md:text-3xl font-bold text-center mb-2 tracking-tight text-foreground">
              COMO FAZER?
            </h3>
            <p className="text-lg md:text-xl text-foreground/60 text-center mb-6 leading-relaxed font-serif italic">
              Seu único trabalho é reviver os bons momentos e escolher suas
              fotos.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <BookOpen className="h-5 w-5 text-secondary" />
                </div>
                <div className="space-y-1">
                  <h4 className="font-semibold text-lg">Escolha o Modelo</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Lúmina, Inspiração, Virtuose e Magnifique
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Layout className="h-5 w-5 text-secondary" />
                </div>
                <div className="space-y-1">
                  <h4 className="font-semibold text-lg">
                    Escolha o formato e o número de páginas
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Opções de capa, tamanho e formato que mais combinam com a
                    sua história.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <CreditCard className="h-5 w-5 text-secondary" />
                </div>
                <div className="space-y-1">
                  <h4 className="font-semibold text-lg">Finalize sua compra</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Você receberá um email com instruções sobre o envio das
                    fotos.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center flex-shrink-0">
                  <Wand2 className="h-5 w-5 text-secondary" />
                </div>
                <div className="space-y-1">
                  <h4 className="font-semibold text-lg">
                    Agora, nossa equipe cuida de tudo:
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Curadoria, design, impressão e envio do álbum para a sua
                    casa.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mb-12">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4 text-foreground">
            Nossos modelos de{' '}
            <span className="font-script text-4xl md:text-5xl text-gold font-normal">
              FotoLivro
            </span>
          </h2>
        </div>

        {products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">
              Nenhum produto disponível no momento.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((product, index) => (
              <Card
                key={product.id}
                className={`overflow-hidden transition-all duration-700 hover:scale-105 hover:shadow-xl flex flex-col ${
                  isVisible
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-10'
                }`}
                style={{
                  transitionDelay: isVisible ? `${index * 150}ms` : '0ms',
                }}
                itemScope
                itemType="https://schema.org/Product"
              >
                <meta itemProp="name" content={`FotoLivro ${product.name}`} />
                <meta itemProp="description" content={product.description} />
                <meta itemProp="brand" content="FotoLivro Store" />
                <meta itemProp="sku" content={product.sku} />
                <div
                  itemProp="offers"
                  itemScope
                  itemType="https://schema.org/Offer"
                >
                  <meta itemProp="price" content={String(product.basePrice)} />
                  <meta itemProp="priceCurrency" content="BRL" />
                  <link
                    itemProp="availability"
                    href="https://schema.org/InStock"
                  />
                  <link
                    itemProp="url"
                    href={`${window.location.origin}/#produtos`}
                  />
                </div>

                {product.images.length > 0 ? (
                  <Carousel className="w-full" opts={{ loop: true }}>
                    <CarouselContent>
                      {product.images.map((image, idx) => (
                        <CarouselItem key={idx}>
                          <div className="aspect-square overflow-hidden bg-background">
                            <img
                              src={image}
                              alt={`${product.name} - Visualização ${idx + 1}`}
                              className="w-full h-full object-cover"
                              itemProp={idx === 0 ? 'image' : undefined}
                              loading="lazy"
                            />
                          </div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                    <CarouselPrevious className="left-2" />
                    <CarouselNext className="right-2" />
                  </Carousel>
                ) : (
                  <div className="aspect-square bg-muted flex items-center justify-center">
                    <p className="text-muted-foreground">Sem imagem</p>
                  </div>
                )}

                <CardHeader className="pb-4 px-6 pt-6">
                  <div className="flex flex-col gap-2 mb-2">
                    <div className="flex items-center justify-between gap-2">
                      <CardTitle className="text-2xl font-serif font-bold tracking-tight uppercase">
                        {product.name}
                      </CardTitle>
                      <span className="text-[10px] font-semibold text-foreground px-2 py-1 bg-muted rounded border border-border whitespace-nowrap flex-shrink-0">
                        {product.level}
                      </span>
                    </div>
                  </div>
                  <CardDescription className="text-sm min-h-[3.5rem] flex items-center leading-relaxed">
                    {product.description}
                  </CardDescription>
                </CardHeader>
                <div className="px-6 pb-4 mt-2">
                  <p className="text-xl font-bold text-primary">
                    {product.price}
                  </p>
                </div>
                <CardContent className="flex-1 pb-4 px-6">
                  <div className="space-y-2">
                    {product.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <Check className="h-4 w-4 text-secondary mt-0.5 flex-shrink-0" />
                        <div>
                          <span
                            className={`text-sm leading-relaxed ${typeof feature === 'string' ? 'text-muted-foreground' : feature.bold ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}
                          >
                            {typeof feature === 'string'
                              ? feature
                              : feature.text}
                          </span>
                          {typeof feature !== 'string' && feature.subtopics && (
                            <ul className="mt-1 ml-1 space-y-0.5">
                              {feature.subtopics.map((sub, subIdx) => (
                                <li
                                  key={subIdx}
                                  className="font-semibold text-foreground text-xs"
                                >
                                  • {sub}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
                <CardFooter className="pt-6 pb-8 px-6 flex-col gap-3">
                  <a href={`/produtos/${product.slug}`} className="w-full">
                    <Button
                      className="w-full gap-2 h-12 text-base font-semibold"
                      size="lg"
                      variant="default"
                    >
                      <ShoppingCart className="h-5 w-5" />
                      Iniciar meu álbum
                    </Button>
                  </a>
                  <div className="w-full flex justify-center">
                    <ShareButtons
                      title={`FotoLivro ${product.name}`}
                      text={`Confira o fotolivro ${product.name} - ${product.description}`}
                      url={`${window.location.origin}/produtos/${product.slug}`}
                    />
                  </div>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

export default ProductGrid
